/**
 * Based on Horizon base origin product-hotspot.js external libs. Since project-specific
 * settings have been made, the external Horizon setup was deprecated in this
 * project and moved to component folder for project-specific settings.
 */

import { Component } from "@theme/component";
import { isClickedOutside } from "@theme/utilities";

/** Popover is for large desktops. Tablet and small desktop open quick add directly. */
const hotspotPopoverQuery = matchMedia("(min-width: 1280px)");

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ["trigger", "dialog"];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  /** @type {(() => void) | null} */
  #pointerleaveHandler = null;
  timer = /** @type {number | null} */ (null);
  /** Invalidates an in-flight open when another hotspot takes over or this one closes. */
  #openSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();

    // Listen for breakpoint changes
    hotspotPopoverQuery.addEventListener("change", this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    hotspotPopoverQuery.removeEventListener("change", this.#handleBreakpointChange);
  }

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector("quick-add-component"));

    if (quickAddComponent) {
      quickAddComponent.handleClick(new MouseEvent("click", { bubbles: true, cancelable: true }));
      return;
    }

    const notifyMeTrigger = /** @type {HTMLButtonElement | null} */ (
      this.querySelector("[data-open-notify-me-picker]")
    );
    if (notifyMeTrigger) {
      notifyMeTrigger.click();
    }
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
    };

    const pointerleaveHandler = () => {
      if (!this.timer) return;

      clearTimeout(this.timer);
      this.timer = null;
    };

    this.#pointerenterHandler = pointerenterHandler;
    this.#pointerleaveHandler = pointerleaveHandler;
    trigger.addEventListener("pointerenter", pointerenterHandler);
    trigger.addEventListener("pointerleave", pointerleaveHandler);
    this.addEventListener("click", this.#openFromClick);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener("pointerenter", this.#pointerenterHandler);
      this.#pointerenterHandler = null;
    }

    if (this.#pointerleaveHandler) {
      trigger.removeEventListener("pointerleave", this.#pointerleaveHandler);
      this.#pointerleaveHandler = null;
    }

    this.removeEventListener("click", this.#openFromClick);

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Open from a click on the hotspot, including the area around the visible dot.
   * Clicks inside the popover are left alone so its actions still work.
   * @param {MouseEvent} event - The click event.
   */
  #openFromClick = (event) => {
    const target = event.target;

    if (!(target instanceof Node) || !this.contains(target)) return;
    if (this.refs.dialog.contains(target)) return;

    event.stopPropagation();
    this.showDialog();
  };

  /**
   * Keep only the most recently opened popover in this module.
   */
  #closeOtherHotspots() {
    const container = this.closest(".hotspot-module") ?? this.parentElement;

    if (!container) return;

    for (const hotspot of container.querySelectorAll("product-hotspot-component")) {
      if (!(hotspot instanceof ProductHotspotComponent) || hotspot === this) continue;

      hotspot.closeDialog();
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    if (hotspotPopoverQuery.matches) {
      this.#setupDesktopListeners();
      return;
    }

    this.closeDialog();
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.closest(`.hotspot-module`) || this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = "hidden";
    dialog.style.display = "block";
    dialog.style.transform = "none";
    dialog.removeAttribute("data-placement");

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty("display");
    dialog.style.removeProperty("visibility");
    dialog.style.removeProperty("transform");
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Calculate available space
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = "right";

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = "right";
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = "left";
    } else {
      x = "center";
    }

    // Determine vertical placement
    let y = "bottom";
    let verticalOffset = 0;

    if (x !== "center") {
      let dialogStartY = buttonTop; // Default to top-aligned
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned overflows bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = "top";

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          // If bottom-aligned overflows bottom
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          // If top-aligned overflows top
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = "bottom";
        }
      }
    } else {
      // For center horizontal: position below or above button
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = "bottom";
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = "top";
      } else {
        // If neither fits well, choose based on button position
        y = buttonTop < containerRect.height / 2 ? "bottom" : "top";
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty("--dialog-vertical-offset", `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty("--dialog-vertical-offset");
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click. Below large desktop, and on touch devices, open quick add.
   * Large desktop clicks bubble to #openFromClick.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    // Check if it's a touch device (tablets) or mobile breakpoint
    const isTouchDevice = matchMedia("(hover: none)").matches;

    if (!hotspotPopoverQuery.matches || isTouchDevice) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
    }
  };

  showDialog = async () => {
    const { dialog } = this.refs;
    const sequence = ++this.#openSequence;

    this.#closeOtherHotspots();

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    if (sequence !== this.#openSequence || !this.isConnected) return;

    dialog.dataset.showing = "true";
    dialog.show();
    document.body.addEventListener("click", this.lightDismissMouse);
    document.body.addEventListener("keydown", this.lightDismissKeyboard);
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#openSequence += 1;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    document.body.removeEventListener("click", this.lightDismissMouse);
    document.body.removeEventListener("keydown", this.lightDismissKeyboard);

    if (!dialog.open) return;

    dialog.dataset.closing = "true";
    dialog.close();
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    const target = event.target;

    if (target instanceof Node && this.contains(target)) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    if (event.key === "Escape") {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define("product-hotspot-component", ProductHotspotComponent);
